import React, { useEffect, useMemo, useState } from 'react';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError } from '../../../../core/alert/sweetAlertConfig';

type ParkingSlotApiData = {
  id: number;
  slotName: string;
  x: string | null;
  y: string | null;
  locationId: number | null;
  floorId: number | null;
  status: string;
  vehicleId: number | null;
  visitorAllocations: unknown | null;
};

interface ViewParkingSlotModuleProps {
  show: boolean;
  onHide: () => void;
  /** Raw API-shaped slot data (nullable while not selected) */
  parkingSlot: ParkingSlotApiData | null;
  /** Optional external loading flag while parent fetches the slot itself */
  isLoading?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  AVAILABLE: 'Available',
  OCCUPIED: 'Occupied',
  RESERVED: 'Reserved',
  PERMANENTLY_ALLOCATED: 'Permanently Allocated',
};

const badgeClass = (status?: string) => {
  switch (status) {
    case 'AVAILABLE': return 'badge bg-success';
    case 'OCCUPIED': return 'badge bg-danger';
    case 'RESERVED': return 'badge bg-warning text-dark';
    case 'PERMANENTLY_ALLOCATED': return 'badge bg-primary';
    default: return 'badge bg-secondary';
  }
};

const ViewParkingSlotModule: React.FC<ViewParkingSlotModuleProps> = ({
  show,
  onHide,
  parkingSlot,
  isLoading = false,
}) => {
  const [floorName, setFloorName] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [isFloorLoading, setIsFloorLoading] = useState<boolean>(false);
  const [isLocationLoading, setIsLocationLoading] = useState<boolean>(false);

  // Reset when viewing a different slot
  useEffect(() => {
    setFloorName(null);
    setLocationName(null);
  }, [parkingSlot?.id]);

  // Fetch floor name by ID
  useEffect(() => {
    const fetchFloor = async () => {
      if (!parkingSlot?.floorId) {
        setFloorName(null);
        return;
      }
      try {
        setIsFloorLoading(true);
        const res = await apiDefinitions.getFloorById(parkingSlot.floorId);
        if (res.status === 200) {
          setFloorName(res?.data?.data?.name ?? null);
        } else {
          setFloorName(null);
          showError(res.data?.statusDescription || 'Failed to fetch floor');
        }
      } catch (e: any) {
        setFloorName(null);
        showError(e?.response?.data?.statusDescription || 'Failed to fetch floor');
      } finally {
        setIsFloorLoading(false);
      }
    };
    fetchFloor();
  }, [parkingSlot?.floorId]);

  // Fetch location name by ID
  useEffect(() => {
    const fetchLocation = async () => {
      if (!parkingSlot?.locationId) {
        setLocationName(null);
        return;
      }
      try {
        setIsLocationLoading(true);
        const res = await apiDefinitions.getLocationById(parkingSlot.locationId);
        if (res.status === 200) {
          setLocationName(res?.data?.data?.name ?? null);
        } else {
          setLocationName(null);
          showError(res.data?.statusDescription || 'Failed to fetch location');
        }
      } catch (e: any) {
        setLocationName(null);
        showError(e?.response?.data?.statusDescription || 'Failed to fetch location');
      } finally {
        setIsLocationLoading(false);
      }
    };
    fetchLocation();
  }, [parkingSlot?.locationId]);

  const displayFloor = useMemo(() => {
    if (!parkingSlot?.floorId) return null;
    if (isFloorLoading) return 'Loading floor…';
    return floorName ?? `Floor #${parkingSlot.floorId}`;
  }, [parkingSlot?.floorId, isFloorLoading, floorName]);

  const displayLocation = useMemo(() => {
    if (!parkingSlot?.locationId) return null;
    if (isLocationLoading) return 'Loading location…';
    return locationName ?? `Location #${parkingSlot.locationId}`;
  }, [parkingSlot?.locationId, isLocationLoading, locationName]);

  const displayLocationFloor = useMemo(() => {
    // Prefer floor if present, otherwise show location, otherwise '-'
    return displayFloor ?? displayLocation ?? '-';
  }, [displayFloor, displayLocation]);

  if (!show) return null;

  return (
    // No custom backdrop; force display to ensure clicks work without Bootstrap JS
    <div className="modal d-block" style={{ display: 'block' }} tabIndex={-1} role="dialog" aria-modal="true">
      <div className="modal-dialog">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">Parking Slot Details</h5>
            <button type="button" className="btn-close" onClick={onHide} aria-label="Close" />
          </div>

          {/* Body */}
          <div className="modal-body">
            {isLoading && (
              <div className="d-flex align-items-center gap-2">
                <div className="spinner-border" role="status" aria-hidden="true" />
                <span>Loading…</span>
              </div>
            )}

            {!isLoading && !parkingSlot && <div className="text-muted">No data available.</div>}

            {!isLoading && parkingSlot && (
              <>
                <p><strong>Slot Name:</strong> {parkingSlot.slotName}</p>

                <p className="mb-2">
                  <strong>Status:</strong>{' '}
                  <span className={badgeClass(parkingSlot.status)}>
                    {STATUS_LABEL[parkingSlot.status] ?? parkingSlot.status}
                  </span>
                </p>

                <p className="mb-1"><strong>Location / Floor:</strong> {displayLocationFloor}</p>Parking Slots

                {/* If you want to show both lines explicitly, uncomment:
                <p className="mb-1"><strong>Location:</strong> {displayLocation ?? '-'}</p>
                <p className="mb-3"><strong>Floor:</strong> {displayFloor ?? '-'}</p>
                */}

                <div className="mt-2">
                  <p className="mb-1">
                    <strong>Vehicle Allocation:</strong>{' '}
                    {parkingSlot.vehicleId ? `Vehicle #${parkingSlot.vehicleId}` : '—'}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={onHide}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewParkingSlotModule;
